import { Observable, OperatorFunction, Subscription } from 'rxjs';

/** Custom Operator that behaves like combineLatestWith but instead of skipping intial values it stores them and emits them when the other observable emits a value. */
export function combineAllWith<T, U>(other$: Observable<U>): OperatorFunction<T, [T, U]> {
	return (source$: Observable<T>) =>
		new Observable<[T, U]>((subscriber) => {
			let sourceFinished = false;

			// store values that are emitted before the other observable emits
			const storedFirstValue: Array<T> = [];
			const storedSecondValue: Array<U> = [];

			let lastFirstValue: T | null | undefined;
			let lastSecondValue: U | null | undefined;

			const sub1 = source$.subscribe({
				next: (value) => {
					// if the other observable has emitted a value, emit the stored value
					if (lastSecondValue !== undefined) {
						// if there are stored values, emit them first
						if (storedSecondValue.length > 0) {
							while (storedSecondValue.length > 0) {
								subscriber.next([value, storedSecondValue.shift()]);
							}
						} else {
							// emit the last value from the other observable
							subscriber.next([value, lastSecondValue]);
						}
					} else {
						// store the value if the other observable has not emitted a value yet
						storedFirstValue.push(value);
					}
					lastFirstValue = value;
				},
				error: (err) => subscriber.error(err),
				complete: () => {
					// if the other observable has emitted a value or the first observable completed before a value was emitted, complete the observable
					if (lastSecondValue !== undefined || lastFirstValue === undefined) {
						subscriber.complete();
					}

					sourceFinished = true;
				},
			});

			let sub2: Subscription | undefined;
			// if the source observable completed before it emitted a value then dont subscribe to the other observable
			if (!subscriber.closed) {
				sub2 = other$.subscribe({
					next: (value) => {
						if (lastFirstValue !== undefined) {
							if (storedFirstValue.length > 0) {
								while (storedFirstValue.length > 0) {
									subscriber.next([storedFirstValue.shift(), value]);
								}
							} else {
								subscriber.next([lastFirstValue, value]);
							}
							// complete the observable if the source observable has finished
							if (sourceFinished) {
								subscriber.complete();
							}
						} else {
							storedSecondValue.push(value);
						}
						lastSecondValue = value;
					},
					error: (err) => subscriber.error(err),
					complete: () => {
						// if the combine observable complete without a value complete the observable
						if (lastSecondValue === undefined) {
							subscriber.complete();
						}
					},
				});
			}

			return () => {
				sub1.unsubscribe();
				sub2?.unsubscribe();
			};
		});
}
